import React, { useCallback, useEffect, useState } from "react";
import TableTitle from "../../components/TableTitle/TableTitle";
import styles from "./styles.module.scss";
import { authApiService } from "../../api/api";
import TerminalLiveCard from "../../components/terminals/TerminalLiveCard/TerminalLiveCard";
import TerminalBleDevicesModal from "../../components/terminals/TerminalBleDevicesModal/TerminalBleDevicesModal";
import { TerminalLockerLiveItem } from "../../types/terminal-locker-live.type";

const POLL_MS = 2500;

const TerminalPage: React.FC = () => {
  const [terminals, setTerminals] = useState<TerminalLockerLiveItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  /** Один відкритий рядок гармошки. `null` — усі згорнуті. */
  const [openTerminalId, setOpenTerminalId] = useState<string | null>(null);
  const [bleModalTerminalId, setBleModalTerminalId] = useState<string | null>(
    null
  );
  const [updatingMaintenanceById, setUpdatingMaintenanceById] = useState<
    Record<string, boolean>
  >({});

  const fetchLive = useCallback(async (): Promise<boolean> => {
    try {
      const data = await authApiService.getTerminalLockerLive();
      if (data) {
        setTerminals(data);
        setError(null);
        setLastUpdated(new Date());
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const ok = await fetchLive();
        if (cancelled) return;
        if (!ok) {
          setError("Не вдалося завантажити дані. Перевірте авторизацію.");
        }
      } catch {
        if (!cancelled) {
          setError("Не вдалося завантажити дані. Перевірте авторизацію.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    const id = window.setInterval(() => {
      fetchLive().then((ok) => {
        if (!cancelled && !ok) {
          setError("Помилка оновлення. Повтор…");
        }
      });
    }, POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [fetchLive]);

  useEffect(() => {
    if (
      bleModalTerminalId &&
      !terminals.some((t) => t.id === bleModalTerminalId)
    ) {
      setBleModalTerminalId(null);
    }
  }, [bleModalTerminalId, terminals]);

  const metaLabel = lastUpdated
    ? `Оновлено: ${lastUpdated.toLocaleString()} · автооновлення ~${POLL_MS / 1000} с`
    : null;

  const bleModalTerminal = bleModalTerminalId
    ? terminals.find((t) => t.id === bleModalTerminalId) ?? null
    : null;

  const handleMaintenanceToggle = async (terminal: TerminalLockerLiveItem) => {
    const terminalId = terminal.id;
    const nextAvailable = !terminal.available;
    setUpdatingMaintenanceById((prev) => ({ ...prev, [terminalId]: true }));
    setTerminals((prev) =>
      prev.map((item) =>
        item.id === terminalId ? { ...item, available: nextAvailable } : item
      )
    );
    try {
      const updated = await authApiService.updateTerminal(terminalId, {
        available: nextAvailable,
      });
      if (!updated) {
        throw new Error("Unauthorized");
      }
      const serverAvailable =
        typeof updated.available === "boolean"
          ? updated.available
          : nextAvailable;
      setTerminals((prev) =>
        prev.map((item) =>
          item.id === terminalId ? { ...item, available: serverAvailable } : item
        )
      );
    } catch {
      setTerminals((prev) =>
        prev.map((item) =>
          item.id === terminalId
            ? { ...item, available: terminal.available }
            : item
        )
      );
      setError("Не вдалося змінити статус технічної перерви.");
    } finally {
      setUpdatingMaintenanceById((prev) => ({ ...prev, [terminalId]: false }));
    }
  };

  return (
    <main className={styles.container}>
      <div className={styles.header}>
        <TableTitle title="Термінали" count={terminals.length} />
        {metaLabel && <span className={styles.meta}>{metaLabel}</span>}
      </div>

      {error && <p className={styles.errorBanner}>{error}</p>}

      {isLoading && (
        <div className={styles.centered}>Завантаження…</div>
      )}

      {!isLoading && terminals.length === 0 && !error && (
        <div className={styles.centered}>
          <p>Наразі немає терміналів для відображення.</p>
        </div>
      )}

      {!isLoading && terminals.length > 0 && (
        <div className={styles.terminalsList}>
          {terminals.map((terminal) => {
            const isOpen = openTerminalId === terminal.id;
            const headId = `term-acc-head-${terminal.id}`;
            const panelId = `term-acc-panel-${terminal.id}`;

            return (
              <section
                key={terminal.id}
                className={styles.accordionItem}
                data-open={isOpen}
              >
                <div className={styles.accordionTopBar}>
                  <button
                    type="button"
                    className={styles.accordionHeader}
                    id={headId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => {
                      setOpenTerminalId((cur) =>
                        cur === terminal.id ? null : terminal.id
                      );
                    }}
                  >
                    <span className={styles.accordionHeaderMain}>
                      <span className={styles.accordionChevron} aria-hidden>
                        ›
                      </span>
                      <span className={styles.accordionTitle}>
                        {terminal.terminal_id}
                      </span>
                    </span>
                    <span
                      className={
                        terminal.connected
                          ? styles.badgeOnline
                          : styles.badgeOffline
                      }
                    >
                      {terminal.connected
                        ? "Підключено (WebSocket)"
                        : "Не в мережі"}
                    </span>
                  </button>
                  <label className={styles.maintenanceToggle}>
                    <input
                      type="checkbox"
                      checked={!terminal.available}
                      disabled={!!updatingMaintenanceById[terminal.id]}
                      onChange={() => {
                        void handleMaintenanceToggle(terminal);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className={styles.maintenanceTrack} aria-hidden />
                    <span className={styles.maintenanceLabel}>
                      Технічна перерва
                    </span>
                  </label>
                  <button
                    type="button"
                    className={styles.bleHeaderButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setBleModalTerminalId(terminal.id);
                    }}
                  >
                    BLE пристрої
                  </button>
                </div>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={headId}
                  className={styles.accordionPanel}
                  hidden={!isOpen}
                >
                  {isOpen ? (
                    <TerminalLiveCard
                      terminal={terminal}
                      embedInAccordion
                      onMetaSaved={() => {
                        void fetchLive();
                      }}
                    />
                  ) : null}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {bleModalTerminal && (
        <TerminalBleDevicesModal
          terminal={bleModalTerminal}
          isOpen
          onClose={() => setBleModalTerminalId(null)}
          onMetaSaved={() => {
            void fetchLive();
          }}
        />
      )}
    </main>
  );
};

export default TerminalPage;
